// Notification bell — a dropdown feed of in-app notifications for the current viewer (owner or
// manufacturer). Shows an unread count; opening the panel marks everything read. Same component is
// reused in the owner header and the canner portal header; the caller supplies the (already
// audience-filtered) list and the mark-read callback.
import { useEffect, useRef, useState } from "react";
import type { Notification } from "@shared/types.ts";

function timeAgo(at: number, now: number): string {
  const s = Math.max(0, Math.round((now - at) / 1000));
  if (s < 60) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

const DOT: Record<string, string> = { ok: "bg-ok", warn: "bg-warn", danger: "bg-danger", info: "bg-brand" };

export function NotificationBell({ notifications, onMarkRead, onOpen }: {
  notifications: Notification[];
  onMarkRead: (ids?: string[]) => Promise<void> | void;
  onOpen?: (n: Notification) => void; // jump to the section (and run) the note is about
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const list = [...notifications].sort((a, b) => b.at - a.at);
  const unread = list.filter((n) => !n.read).length;
  const now = Date.now();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const openPanel = () => {
    setOpen((o) => !o);
    if (!open && unread > 0) void onMarkRead(); // opening clears the unread badge
  };

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={openPanel} aria-label="Notifications"
        className="relative w-8 h-8 inline-flex items-center justify-center rounded-lg border border-edge text-muted hover:text-ink hover:bg-paper">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>
        {unread > 0 && <span className="absolute -top-1 -right-1 min-w-4 h-4 px-1 rounded-full bg-danger text-white text-[10px] font-semibold grid place-items-center">{unread > 9 ? "9+" : unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 top-10 z-50 w-80 max-w-[calc(100vw-1.5rem)] bg-panel rounded-2xl shadow-pop border border-edge overflow-hidden animate-fade-in-up">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-edge">
            <span className="text-sm font-semibold">Notifications</span>
            {list.length > 0 && <button className="text-[11px] text-muted hover:text-ink" onClick={() => void onMarkRead()}>Mark all read</button>}
          </div>
          <div className="max-h-[70vh] overflow-y-auto divide-y divide-edge/60">
            {list.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-muted">No notifications yet.</div>
            ) : list.slice(0, 60).map((n) => (
              <button key={n.id} type="button" disabled={!onOpen}
                onClick={() => { setOpen(false); onOpen?.(n); }}
                className={`w-full text-left flex gap-2.5 px-4 py-2.5 ${n.read ? "" : "bg-brand/5"} ${onOpen ? "hover:bg-paper transition" : "cursor-default"}`}>
                <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${DOT[n.tone || "info"]}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium leading-snug">{n.title}</div>
                  {n.body && <div className="text-xs text-muted mt-0.5 leading-snug">{n.body}</div>}
                  <div className="text-[10px] text-muted/70 mt-0.5">{timeAgo(n.at, now)}</div>
                </div>
                {onOpen && <span className="text-muted/50 text-xs self-center shrink-0">›</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
